<template>
  <div class="timetable-content main-content wide">
    <div class="wrap">
      <h1>{{ pageData.pageTitle }}</h1>
      <div
        v-dompurify-html="pageData.description"
        class="page-subtitle"
      />
      <search-form-timetable @getTimetable="getTimetable" />
    </div>
    <div
      ref="search-results"
      class="timetable-search-results"
    >
      <div class="wrap">
        <print-button
          v-if="isDesktop && formSubmitted"
          class="ghost"
        />
        <timetable
          v-if="formSubmitted && calendar.departureDate !== ''"
          class="departure"
          :flight-title="pageData.departureFlightTitle"
          :route="`${origTexts} — ${destTexts}`"
          :flight-type="'departure'"
          :data="departureData"
          :flight-day="calendar.departureDate"
          :selected-sorter="departureSorter"
          :selected-date="selectedDeparture"
          :no-data-text="pageData.noResultsText"
          :min-date="minDateDeparture"
          @getTimetable="getTimetable"
        />

        <timetable
          v-if="formSubmitted && tripType === 'return' && calendar.returnDate !== ''"
          class="return"
          :flight-title="pageData.returnFlightTitle"
          :route="`${destTexts} — ${origTexts}`"
          :flight-type="'return'"
          :data="returnData"
          :flight-day="calendar.returnDate"
          :selected-sorter="returnSorter"
          :selected-date="selectedReturn"
          :no-data-text="pageData.noResultsText"
          :min-date="minDateReturn"
          @getTimetable="getTimetable"
        />

        <div
          v-if="!formSubmitted"
          class="no-search"
        >
          {{ pageData.noSearchDoneText }}
        </div>
      </div>
    </div>

    <div
      v-if="pageData.documents.length"
      class="download-block"
    >
      <h3>{{ pageData.pdfBlockTitle }}</h3>
      <div class="btn-group wrap">
        <div
          v-for="(link, index) in pageData.documents"
          :key="index"
          class="btn btn-blue"
        >
          <a
            :href="link.url"
            target="_blank"
          >
            <span>{{ link.title }}</span>
          </a>
        </div>
      </div>
    </div>
    <div
      v-show="selectedDeparture.date && (selectedReturn.date || tripType === 'oneway')"
      class="selected-flights-block"
    >
      <div class="wrap">
        <div class="selected-flights-content">
          <template v-if="showBottomBlock">
            <div
              v-if="selectedDeparture"
              class="selected-departure-flight"
            >
              <span>
                <strong>{{ selectedDeparture.date }}</strong>
                <br />
                {{ selectedDepartureInfo }}
              </span>
              <div
                v-if="!isMobile"
                class="duration"
              >
                <span> {{ $t('reusable.sorters.flightDuration') }}: </span>
                {{ selectedDeparture.duration }}
              </div>
              <div
                v-if="!isMobile && departureConnection !== 0"
                class="duration"
              >
                <span>{{ pageData.connection }}:</span>
                {{ departureConnection }}
              </div>
            </div>
            <div
              v-if="selectedReturn && tripType === 'return'"
              class="selected-return-flight"
            >
              <span>
                <strong>{{ selectedReturn.date }}</strong>
                <br />
                {{ selectedReturnInfo }}
              </span>
              <div
                v-if="!isMobile"
                class="duration"
              >
                <span>{{ $t('reusable.sorters.flightDuration') }}:</span>
                {{ selectedReturn.duration }}
              </div>
              <div
                v-if="!isMobile && returnConnection !== 0"
                class="duration"
              >
                <span>{{ pageData.connection }}:</span>
                {{ returnConnection }}
              </div>
            </div>
          </template>
          <div class="btn btn-white blue-text">
            <a :href="checkPricesLink">
              {{ pageData.checkPrices }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import SearchFormTimetable from 'containers/SearchForm/SearchFormTimetable'
  import PrintButton from 'components/Buttons/PrintButton'
  import Timetable from 'components/Tables/Timetable'
  import conf from 'conf'
  import Vue from 'vue'
  import ScrollUtils from 'utils/scrollUtils'

  export default {
    components: {
      SearchFormTimetable,
      PrintButton,
      Timetable
    },
    data() {
      return {
        sorterItems: [
          {
            value: 'departure',
            title: this.$t('reusable.sorters.departureTime')
          },
          {
            value: 'arrival',
            title: this.$t('reusable.sorters.arrivalTime')
          },
          {
            value: 'duration',
            title: this.$t('reusable.sorters.flightDuration')
          }
        ]
      }
    },
    computed: {
      showBottomBlock() {
        return this.formSubmitted && this.selectedDeparture && this.selectedDeparture.date
      },
      destTexts() {
        return (
          this.formSubmitted.destination.city +
          ' (' +
          this.formSubmitted.destination.code +
          ')'
        )
      },
      origTexts() {
        return (
          this.formSubmitted.origin.city + ' (' + this.formSubmitted.origin.code + ')'
        )
      },
      selectedDepartureInfo() {
        return (
          this.formSubmitted.origin.code +
          ' ' +
          this.selectedDeparture.departTime +
          ' – ' +
          this.formSubmitted.destination.code +
          ' ' +
          this.selectedDeparture.arrivalTime
        )
      },

      selectedReturnInfo() {
        return (
          this.formSubmitted.destination.code +
          ' ' +
          this.selectedReturn.departTime +
          ' – ' +
          this.formSubmitted.origin.code +
          ' ' +
          this.selectedReturn.arrivalTime
        )
      },

      minDateDeparture() {
        return moment().format('DD.MM.YYYY')
      },

      minDateReturn() {
        return this.selectedDeparture.date || this.minDateDeparture
      },

      departureConnection() {
        return this.selectedDeparture.connectionTime
          ? this.selectedDeparture.connectionTime + ' ' + this.$t('timetable.in')
          : 0
      },

      returnConnection() {
        return this.selectedReturn.connectionTime
          ? this.selectedReturn.connectionTime + ' ' + this.$t('timetable.in')
          : 0
      },

      checkPricesLink() {
        if (!this.showBottomBlock) {
          return ''
        }

        if (this.paxSelector.groupBooking) {
          return (
            this.groupBookingUrl +
            '?fromCode=' +
            this.formSubmitted.origin.code +
            '&fromType=' +
            this.formSubmitted.origin.type +
            '&toCode=' +
            this.formSubmitted.destination.code +
            '&toType=' +
            this.formSubmitted.destination.type +
            '&tripType=' +
            this.tripType +
            '&numberOfAdults=' +
            this.paxSelector.adults +
            '&numberOfChildren=' +
            this.paxSelector.children +
            '&numberOfInfants=' +
            this.paxSelector.infants
          )
        }
        let url =
          conf.newIbeUrl +
          '/' +
          Vue.i18n.locale() +
          '/fb/availability' +
          '?' +
          'originCode=' +
          this.formSubmitted.origin.code +
          '&destinCode=' +
          this.formSubmitted.destination.code +
          '&tripType=' +
          this.tripType +
          '&departure=' +
          moment(this.selectedDeparture.date, 'DD.MM.YYYY').format('YYYY-MM-DD') +
          (this.tripType === 'return'
            ? '&return=' +
              moment(this.selectedReturn.date, 'DD.MM.YYYY').format('YYYY-MM-DD')
            : '') +
          '&numAdt=' +
          this.paxSelector.adults

        if (this.paxSelector.children > 0) {
          url += '&numChd=' + this.paxSelector.children
        }

        if (this.paxSelector.infants > 0) {
          url += '&numInf=' + this.paxSelector.infants
        }

        url += '&sref=TTB'

        return url
      },
      ...mapGetters('device', ['isMobile', 'isDesktop']),
      ...mapState('timetable', [
        'formSubmitted',
        'departureData',
        'returnData',
        'departureSorter',
        'returnSorter',
        'selectedDeparture',
        'selectedReturn',
        'pageData'
      ]),
      ...mapState('eHorizonSearchForm', [
        'paxSelector',
        'tripType',
        'destin',
        'origin',
        'calendar',
        'groupBookingUrl'
      ]),
      ...mapState('main', ['selectedLang'])
    },
    methods: {
      getTimetable() {
        if (this.isMobile) {
          const headerHeight =
            document.getElementsByClassName('header-wrap-white')?.[0]?.offsetHeight || 0

          ScrollUtils.smoothScroll(
            this.$refs['search-results'].offsetTop - headerHeight,
            300
          )
        }

        const request = [this.$store.dispatch('timetable/getTimetableDeparture')]

        if (this.tripType === 'return') {
          request.push(this.$store.dispatch('timetable/getTimetableReturn'))
        }

        return Promise.all(request)
          .then((response) => {
            return this.$store
              .dispatch('timetable/timetableParse', response)
              .then((data) => Promise.resolve(data))
          })
          .catch(() => {})
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .timetable-content {
    padding-top: 41px;
    background: linear-gradient(
      to bottom,
      $gray-blue-light 0%,
      $gray-light-background 660px
    );

    @include bp-tablet-only {
      padding-top: 56px;
      background: linear-gradient(
        to bottom,
        $gray-blue-light 0%,
        $gray-light-background 550px
      );
    }

    @include bp-mf-desktop {
      padding-top: 66px;
      background: linear-gradient(
        to bottom,
        $gray-blue-light 0%,
        $gray-light-background 460px
      );
    }

    @media print {
      background: white;
    }

    .wrap {
      padding: 0 23px;
      width: 100%;

      @include bp-tablet-only {
        padding: 0 26px;
      }

      @include bp-mf-desktop {
        padding: 0;
      }

      @media screen and (max-width: 1160px) and (min-width: 992px) {
        padding: 0 26px;
      }

      @media print {
        padding: 0;
      }
    }

    h1 {
      margin: 0 0 13px;
      font-size: em(32);
      line-height: 38px;
      font-weight: 300;

      @include bp-mf-tablet {
        margin-bottom: 19px;
        font-size: em(40);
        line-height: 50px;
        text-align: center;
      }
    }

    .page-subtitle {
      margin: 0 auto 20px;
      line-height: 20px;

      @include bp-mf-tablet {
        max-width: 475px;
        font-size: em(16);
        line-height: 24px;
        text-align: center;
      }
    }
  }

  .download-block {
    padding: 32px 23px 40px;
    background-color: #ffffff;

    @include bp-tablet-only {
      padding: 50px 26px;
    }

    @include bp-mf-tablet {
      text-align: center;
    }

    @include bp-mf-desktop {
      padding: 60px 0;
    }

    @media print {
      display: none;
    }

    h3 {
      margin: 0 0 21px;
      font-size: em(16);
      line-height: 19px;
      font-weight: 500;

      @include bp-mf-tablet {
        margin-bottom: 35px;
        font-size: em(20);
        line-height: 24px;
      }
    }
  }

  .btn-group {
    display: inline-grid;

    @include bp-mf-tablet {
      display: block;
      margin: 0 auto;
    }

    @include bp-mf-desktop {
      padding: 0;
    }

    .btn-blue {
      position: relative;
      min-width: unset;
      height: 52px;
      text-align: left;
      padding: 0 15px;

      &:not(:last-child) {
        margin-bottom: 16px;

        @include bp-tablet-only {
          margin: 0 10px 0 0;
        }

        @include bp-mf-desktop {
          margin: 0 20px 0 0;
        }
      }

      a {
        font-size: em(12);
        line-height: 52px;

        span {
          padding: 0;
          font-weight: 500;
        }
      }
    }
  }

  .selected-flights-block {
    padding: 20px 0;
    background-color: $blue-medium;

    @include bp-mf-tablet {
      padding: 0;
      display: flex;
      align-items: center;
    }
  }

  .selected-flights-content {
    color: #ffffff;

    @include bp-mf-tablet {
      position: relative;
      padding: 18px 180px 28px 0;
    }

    > div:not(.btn-white) {
      @include bp-mf-tablet {
        display: inline-block;
        vertical-align: middle;
      }

      strong {
        font-weight: 500;
      }
    }
  }

  .selected-departure-flight,
  .selected-return-flight {
    display: inline-block;

    &:before {
      @include filled-icon-font();
      vertical-align: middle;
      margin-right: 4px;
      width: 24px;
      height: 24px;
      background-color: hexToRGBA(#ffffff, 0.21);
      border-radius: 50%;
      line-height: 26px;
      text-align: center;

      @include bp-mf-tablet {
        margin-right: 17px;
        width: 36px;
        height: 36px;
        font-size: em(16);
        line-height: 38px;
      }
    }

    > span {
      display: inline-block;
      vertical-align: middle;
      font-size: em(10);
      line-height: 14px;

      @include bp-mf-tablet {
        font-size: em(14);
        line-height: 20px;
      }
    }
  }

  .selected-departure-flight {
    margin-right: 11px;

    @include bp-mf-tablet {
      margin-right: 25px;
      padding-right: 25px;
      border-right: 1px solid hexToRGBA(#ffffff, 0.1);
    }

    @include bp-mf-desktop {
      margin-right: 40px;
      padding-right: 40px;
    }

    &:before {
      content: $filled-icon-flights;
    }
  }

  .selected-return-flight {
    &:before {
      content: $filled-icon-plane-return;
    }
  }

  .selected-flight-time {
    @include bp-tablet-only {
      margin-top: 16px;
      font-size: em(12);
    }
  }

  .duration {
    line-height: 20px;

    span {
      opacity: 0.4;
    }
  }

  .btn-white {
    margin-top: 11px;
    height: 44px;

    @include bp-mf-tablet {
      position: absolute;
      right: 0;
      top: calc(50% - 26px);
      margin-top: 0;
      min-width: 160px;
      max-width: 170px;
      height: 52px;
      line-height: 52px;
    }

    a {
      padding: 0 16px;
      line-height: 44px;
      color: $blue-medium;

      @include bp-mf-tablet {
        font-size: em(16);
        line-height: 52px;
      }
    }
  }

  .timetable-search-results {
    padding-bottom: 17px;
    border-top: 1px solid $timetable-gray-border;
    margin-top: 22px;

    @include bp-mf-tablet {
      padding-bottom: 15px;
      margin-top: 40px;
    }

    @include bp-mf-desktop {
      margin-top: 56px;
    }

    > .wrap {
      position: relative;
    }

    .btn-print {
      top: 40px;

      @media screen and (max-width: 1160px) and (min-width: 992px) {
        right: 26px;
      }
    }

    .no-search {
      padding: 17px 44px 0;
      line-height: 18px;
      text-align: center;
      color: $grey;

      @include bp-mf-tablet {
        padding: 45px 0 30px;
        font-size: em(16);
        line-height: 24px;
      }
    }
  }
</style>
